import { Button, Typography } from '@mui/material';
import { get } from 'lodash';
import { ComponentType } from 'react';
import { useTranslation } from 'react-i18next';
import { DataTable, DataTableFilter } from 'src/components/data-table';
import { Page } from 'src/components/page';
import { getFullName, getTranslatedValue } from '../../../common/utils/format-utils';
import { useAuth, useDialog, useResources } from '../../../hooks';
import { getUsers } from '../../../thunks/user-thunks';
import { DataTableFooter } from '../../data-table/data-table-footer';
import { PlusIcon } from '../../icons';
import { UsersEditDialog } from '../users-edit-dialog';
import { UsersTableActions } from '../users-table-actions';

export const UsersList: ComponentType = () => {
  const { t, i18n } = useTranslation();
  const [openUsersEdit, closeUsersEdit] = useDialog(UsersEditDialog);
  const { isSuperAdmin } = useAuth();
  const {
    list: { hits, isLoading, total },
    query,
    handleQueryUpdate,
    handleFetchFirstPage,
    handleFetchCurrentPage,
  } = useResources({
    entity: 'user',
    onGetAll: getUsers,
    defaultParams: { embed: 'organization' },
  });

  const handleCreate = () => {
    openUsersEdit({
      title: t('users.add', 'Добавление пользователя'),
      onSubmit: () => {
        closeUsersEdit();
        handleFetchFirstPage();
      },
    });
  };

  const handleEdit = (id: string | number) => {
    openUsersEdit({
      title: t('users.edit', 'Редактировать пользователя'),
      id,
      onSubmit: () => {
        closeUsersEdit();
        handleFetchCurrentPage();
      },
    });
  };

  const data = {
    hits,
    isLoading,
    columns: [
      {
        width: 120,
        label: '№',
        labelKey: '',
        renderItem: (item: Record<string, any>) => (
          <Typography variant="h5" color="primary">
            {item.id}
          </Typography>
        ),
      },
      {
        width: 300,
        label: 'ФИО',
        labelKey: 'name.full',
        renderItem: (item: Record<string, any>) => getFullName(item),
      },
      {
        width: 500,
        label: 'Организация',
        labelKey: 'organization',
        hidden: !isSuperAdmin,
        renderItem: (item: Record<string, any>) =>
          getTranslatedValue(get(item, 'organization.title', null), i18n.language),
      },
      {
        width: 'auto',
        label: 'Роль',
        labelKey: 'role',
        renderItem: (item: Record<string, any>) => t(`role.${item.role}`),
      },
      {
        width: 96,
        label: '',
        labelKey: '',
        renderItem: (item: Record<string, any>) => (
          <UsersTableActions item={item} onEdit={handleEdit} />
        ),
      },
    ],
  };

  return (
    <Page
      title={t('users', 'Пользователи')}
      topButton={
        <Button
          onClick={handleCreate}
          variant="contained"
          color="secondary"
          startIcon={<PlusIcon />}
          size="small"
        >
          {t('', 'Добавить пользователя')}
        </Button>
      }
    >
      <DataTableFilter
        {...{
          value: query.search,
          role: query.role,
          onUpdate: handleQueryUpdate,
          SearchFieldProps: {
            placeholder: t('action.search.by.users', 'Ф.И.О., ПИНФЛ, серия и номер паспорта'),
          },
        }}
      />
      <DataTable {...data} />

      <DataTableFooter {...{ total, query, onUpdate: handleQueryUpdate }} />
    </Page>
  );
};
